import { apis } from "../constants/apis";
import httpService from "./httpService";

/**
 * Current user profile as returned by `GET /<prefix>/api/v1/users/me` — a Kratos
 * identity (`traits`, `verifiable_addresses`, ...) plus a `phones` list. Only the
 * fields actually read are typed; the rest stay under the index signature.
 */
export interface ICurrentUser
{
  id?: string;
  email?: string;
  phone?: string;
  phone_number?: string;
  /** [{ phone_number: "0324587963", prefix: 84, area_code: "VN", is_primary: true }] */
  phones?: any[];
  traits?: any;
  [key: string]: any;
}

/**
 * Fetches the logged-in user's profile. Returns undefined when the call fails —
 * the axios response interceptor resolves non-401 errors with the error object
 * instead of rejecting, so the response shape is checked rather than trusted.
 */
export async function getCurrentUser(): Promise<ICurrentUser | undefined>
{
  try
  {
    const res: any = await httpService.get(apis.user.me);
    const data = res?.data?.data ?? res?.data;
    return data && typeof data === "object" && !Array.isArray(data)
      ? (data as ICurrentUser)
      : undefined;
  } catch
  {
    return undefined;
  }
}

/** Keys carrying a number directly on the profile (or its `traits`). */
const PHONE_KEYS = [
  "phone",
  "phone_number",
  "phoneNumber",
  "mobile",
  "mobile_phone",
  "mobilePhone",
  "telephone",
  "tel",
];

/** Keys carrying the number inside an entry of a `phones` list. */
const PHONE_ENTRY_KEYS = ["phone_number", "phoneNumber", "phone", "number", "value"];

const readFirstKey = (source: any, keys: string[]): string =>
{
  for (const key of keys)
  {
    const value = source?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }

  return "";
};

/** The primary (or first) entry of the profile's `phones` list. */
const pickPhoneEntry = (user: ICurrentUser): any =>
{
  const sources = [user, user.traits].filter(Boolean) as Record<string, any>[];

  for (const source of sources)
  {
    const list = source?.phones ?? source?.phone_numbers;
    if (Array.isArray(list) && list.length)
    {
      return list.find((p) => p?.is_primary) ?? list[0];
    }
  }

  return undefined;
};

/**
 * Picks the phone number out of the `phones` list the identity service returns
 * (primary entry first), falling back to a flat key on the profile or `traits`.
 */
export const getUserPhone = (user?: ICurrentUser): string =>
{
  if (!user) return "";

  const entry = pickPhoneEntry(user);
  if (typeof entry === "string" && entry.trim()) return entry.trim();

  const fromList = readFirstKey(entry, PHONE_ENTRY_KEYS);
  if (fromList) return fromList;

  const sources = [user, user.traits].filter(Boolean) as Record<string, any>[];
  for (const source of sources)
  {
    const flat = readFirstKey(source, PHONE_KEYS);
    if (flat) return flat;
  }

  return "";
};

/** Keys carrying the country calling code alongside a number. */
const PREFIX_KEYS = ["prefix", "country_code", "countryCode", "dial_code", "dialCode"];

/**
 * `tel:` target for the profile's phone. Uses the international form when the
 * entry carries a calling code (`prefix: 84` + `"0324587963"` -> `+84324587963`)
 * so the link also dials from outside the country; falls back to the raw digits.
 */
export const getUserPhoneHref = (user?: ICurrentUser): string =>
{
  const number = getUserPhone(user);
  if (!number) return "";

  const digits = number.replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return `tel:${digits}`;

  const prefix = user ? readFirstKey(pickPhoneEntry(user), PREFIX_KEYS) : "";
  if (!prefix) return `tel:${digits}`;

  const code = prefix.replace(/\D/g, "");
  if (!code) return `tel:${digits}`;

  // A number already carrying its country code just needs the leading "+".
  return digits.startsWith(code)
    ? `tel:+${digits}`
    : `tel:+${code}${digits.replace(/^0+/, "")}`;
};

